import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Trophy, Award, Lightbulb, Star,
  ChevronLeft, ChevronRight, School, Users, BookOpen,
} from "lucide-react";
import "./SuccessStories.scss";

import photo1 from "../assets/success-stories/ats-archana.webp";
import photo2 from "../assets/success-stories/aman.webp";
import photo3 from "../assets/Our-projects/vr for school .webp";
import photo4 from "../assets/Our-projects/pm shri schools.webp";
import photo5 from "../assets/Our-projects/youth-skilling.webp";
import photo6 from "../assets/Our-projects/ai-thinktank.webp";

const STORIES = [
  {
    id: 1, photo: photo1, icon: Trophy,
    badge: "4th Place — IIT Bombay Tech Fest",
    name: "Team ATS Karchhana", programme: "Nurture Programme",
    text: "Triumphed at IIT Bombay's prestigious Tech Fest with an innovative robotics project — built through months of rigorous mentoring and teamwork.",
  },
  {
    id: 2, photo: photo2, icon: Award,
    badge: "1st Place — Vigyan Ghar National Science Show",
    name: "Aman & Arpit", programme: "Cograd School",
    text: "Renewable energy research earned first place at a national Science Talent Show, securing a scholarship and putting Cograd on the national map.",
  },
  {
    id: 3, photo: photo3, icon: Lightbulb,
    badge: "Digital Champion — Thingqbator",
    name: "Rural Innovators", programme: "Cisco Thingqbator Programme",
    text: "From a rural home to global platforms — gained lifetime access to new-gen tech courses and built real AI solutions that matter.",
  },
  {
    id: 4, photo: photo4, icon: School,
    badge: "PM SHRI School — Top Performer",
    name: "Cograd PM SHRI Team", programme: "PM SHRI Schools",
    text: "Helped PM SHRI schools achieve top-tier performance ratings by integrating smart classrooms, AI tools, and NEP-aligned teaching strategies.",
  },
  {
    id: 5, photo: photo5, icon: Users,
    badge: "Top 10 — NASSCOM Youth Skilling",
    name: "Mohammed Faiz", programme: "Youth Skilling Programme",
    text: "Trained in AI and Data Science through CoGrad's NASSCOM programme — secured a tech placement within 3 months from a small town with big dreams.",
  },
  {
    id: 6, photo: photo6, icon: BookOpen,
    badge: "National Hackathon Finalist — KVS",
    name: "Vidya Setu Champions", programme: "AI Vidya Setu",
    text: "KVS students built real-world AI solutions at the national hackathon — competing with the best and proving that innovation knows no boundary.",
  },
];

function Card({ s, i }) {
  const Icon = s.icon;
  return (
    <motion.div
      className="ss__card"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: i * 0.06, ease: "easeOut" }}
    >
      <div className="ss__img-wrap">
        <img src={s.photo} alt={s.name} className="ss__photo" loading="lazy" />
        <div className="ss__img-overlay" />
        <div className="ss__badge">
          <Icon size={11} strokeWidth={2.5} />
          <span>{s.badge}</span>
        </div>
      </div>

      <div className="ss__body">
        <div className="ss__stars">
          {[...Array(5)].map((_, k) => (
            <Star key={k} size={11} fill="#f59e0b" color="#f59e0b" />
          ))}
        </div>
        <p className="ss__text">{s.text}</p>
        <div className="ss__divider" />
        <div className="ss__who">
          <div className="ss__avatar">{s.name.charAt(0)}</div>
          <div>
            <span className="ss__name">{s.name}</span>
            <span className="ss__prog">{s.programme}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

const CARD_WIDTH = 310; // card width + gap

export default function SuccessStories() {
  const trackRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const scroll = (dir) => {
    if (!trackRef.current) return;
    const next = Math.max(0, Math.min(STORIES.length - 1, activeIdx + dir));
    trackRef.current.scrollTo({ left: next * CARD_WIDTH, behavior: "smooth" });
    setActiveIdx(next);
  };

  const handleScroll = () => {
    if (!trackRef.current) return;
    const idx = Math.round(trackRef.current.scrollLeft / CARD_WIDTH);
    setActiveIdx(idx);
  };

  return (
    <section className="ss-wrap">
      <div className="ss-inner">

        <motion.div
          className="ss__top"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <div className="ss__chip">✦ IMPACT STORIES</div>
          <h2 className="ss__h2">
            Stories of <span>Success</span>, Hope &amp; Transformation
          </h2>
          <div className="ss__rule" />
          <p className="ss__sub">Real students, real results — powered by Cograd.</p>
        </motion.div>

        <div className="ss__scroll-row">
          <button
            className={`ss__arrow${activeIdx === 0 ? " ss__arrow--disabled" : ""}`}
            onClick={() => scroll(-1)}
            aria-label="Previous"
          >
            <ChevronLeft size={20} />
          </button>

          <div className="ss__track-wrap">
            <div className="ss__track" ref={trackRef} onScroll={handleScroll}>
              {STORIES.map((s, i) => <Card key={s.id} s={s} i={i} />)}
            </div>
          </div>

          <button
            className={`ss__arrow${activeIdx === STORIES.length - 1 ? " ss__arrow--disabled" : ""}`}
            onClick={() => scroll(1)}
            aria-label="Next"
          >
            <ChevronRight size={20} />
          </button>
        </div>


      </div>
    </section>
  );
}
